import React, { useState } from "react";
import { Link } from "react-router-dom";

export default function ForgotPassword() {
  const [id, setId] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [msg, setMsg] = useState("");

  const endpoint = "https://dev.sliitleo.org/Backend/update_password_to_nic.php";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    const sliit_id = id.trim().toUpperCase();

    if (!sliit_id) {
      setStatus("error");
      setMsg("Please enter your SLIIT ID.");
      return;
    }

    // keep simple but helpful format check
    if (!/^[A-Z]{2}\d{6,}$/.test(sliit_id)) {
      setStatus("error");
      setMsg("That SLIIT ID looks unusual. Please double-check.");
      return;
    }

    setStatus("loading");
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sliit_id }),
      });

      // Always try to parse JSON; if not JSON, show trimmed text snippet
      let data = {};
      try {
        data = await res.json();
      } catch {
        const text = await res.text();
        throw new Error(
          `Server error ${res.status}. ${text.slice(0, 140)}`
        );
      }

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || `HTTP ${res.status}`);
      }

      setStatus("success");
      setMsg(data?.message || "Password has been updated to NIC successfully.");
    } catch (err) {
      setStatus("error");
      setMsg(err?.message || "Something went wrong. Please try again.");
    }
  };

  const isLoading = status === "loading";

  return (
    <div className="min-h-screen w-full bg-[#0b1221] text-white grid place-items-center px-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,.55)]">
          {/* Top bar / brand */}
          <div className="bg-gradient-to-r from-[#17233d] to-[#101a31] px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-xl bg-[#1d2a49] grid place-items-center">
                <span className="h-2.5 w-2.5 rounded-full bg-[#60a5fa]" />
              </div>
              <div>
                <h2 className="text-lg font-extrabold leading-tight">Reset Password</h2>
                <p className="text-xs text-[#9fb4d9]">NIC-based reset (securely hashed)</p>
              </div>
            </div>
            <Link
              to="/login"
              className="text-xs px-3 py-1.5 rounded-lg bg-[#1b2a48] hover:bg-[#213257] text-[#cfe0ff]"
            >
              Login
            </Link>
          </div>

          {/* Form area */}
          <div className="bg-[#111b31] px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="sliitid" className="block mb-1 text-sm text-[#cfe0ff]">
                  SLIIT ID
                </label>
                <input
                  id="sliitid"
                  type="text"
                  autoComplete="username"
                  placeholder="e.g. IT21808784"
                  value={id}
                  onChange={(e) => setId(e.target.value.toUpperCase())}
                  disabled={isLoading}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#0e172a] text-[#eaf2ff] placeholder-[#8aa4d1] outline-none border border-transparent focus:border-[#60a5fa] disabled:opacity-60"
                />
                <p className="mt-1 text-[11px] text-[#8aa4d1]">
                  We’ll set your password to your <span className="text-[#ffd54a]">NIC</span> (stored hashed).
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-[#60a5fa] text-[#0b1221] hover:bg-[#3b82f6] disabled:opacity-60"
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                    Processing…
                  </>
                ) : (
                  "Reset to NIC"
                )}
              </button>
            </form>

            {/* Status banner */}
            <div className="mt-4" aria-live="polite">
              {status === "success" && (
                <div className="px-3 py-2 rounded-lg text-sm bg-emerald-600/15 text-emerald-200">
                  {msg}
                </div>
              )}
              {status === "error" && (
                <div className="px-3 py-2 rounded-lg text-sm bg-rose-600/15 text-rose-200">
                  {msg}
                </div>
              )}
            </div>

            {/* After success: show Go to Login button */}
            {status === "success" && (
              <div className="mt-4 grid gap-2">
                <Link
                  to="/login"
                  className="w-full text-center px-4 py-2.5 rounded-lg font-semibold bg-[#ffd54a] text-[#0b1221] hover:bg-[#f6c437]"
                >
                  Go to Login
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setId("");
                    setStatus("idle");
                    setMsg("");
                  }}
                  className="w-full px-4 py-2.5 rounded-lg font-semibold bg-[#1b2a48] text-[#cfe0ff] hover:bg-[#22345a]"
                >
                  Reset another ID
                </button>
              </div>
            )}

            {/* Footer tip */}
            <div className="mt-6 text-[12px] text-[#9fb4d9]">
              <p>
                If you still can’t log in, your NIC may be missing in your profile.
                Please contact an administrator to update it.
              </p>
            </div>
          </div>
        </div>

        {/* Tiny footer */}
        <div className="text-center mt-4 text-[11px] text-[#7ea0db]">
          SLIIT Leo Club Portal 4.0
        </div>
      </div>
    </div>
  );
}
